import type { Campaign, CampaignType } from 'models/Campaign';
import { type FC, useState } from 'react';
import { Button, CampaignModal, ExpandableText } from 'components';
import { FiPlay, FiSettings } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { FaClock, FaPause } from 'react-icons/fa';
import { useToggleLaunchCampaign } from 'pages/Campaigns/hooks/useToggleLauchCampaign';
import useQueryParams from 'hooks/useQueryParams';

import './CampaignCard.styles.scss';

type CampaignCardProps = {
  campaign: Campaign;
  hasEdit?: boolean;
  showPrompt?: boolean;
  onToggleLaunch?: (campaignId: string) => void;
};

const CampaignCard: FC<CampaignCardProps> = ({
  campaign,
  hasEdit = false,
  showPrompt = false,
}) => {
  const { name, scrapeKeywords, queryPrompt, id, isLaunched } = campaign;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { params } = useQueryParams();
  const { toggleLaunch, isPending } = useToggleLaunchCampaign({
    campaign,
    isPersonal: !!params?.campaignType,
  });

  const { campaignType } = params || {};

  const handleToggleLaunch = () => {
    toggleLaunch();
  };

  const statusClass = isLaunched ? 'campaign--launched' : 'campaign--paused';
  const themeColor = isLaunched ? 'green' : 'gray';
  const gradientClass = 'blue';

  return (
    <div className={`campaign-card ${statusClass} gradient-${gradientClass}`}>
      <div className="campaign-card__header">
        <div className="campaign-card__top">
          <div className="campaign-card__info">
            <div className="campaign-card__status">
              <div className={`status-dot ${themeColor}`} />
              <span className={`status-text ${themeColor}`}>
                {isLaunched ? 'Launched' : 'Paused'}
              </span>
            </div>
            <h2 className="campaign-card__title">{name}</h2>
          </div>
          <div className="campaign-card__actions">
            {hasEdit ? (
              <Button
                variant="icon"
                className={`edit-button ${gradientClass}`}
                onClick={() => setEditModalOpen(true)}
              >
                <FiSettings className={`icon icon-${gradientClass}`} />
              </Button>
            ) : (
              <Link
                to={`${id}${
                  campaignType ? `?campaignType=${campaignType}` : ''
                }`}
                className={`details-link ${gradientClass}`}
              >
                View Details →
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="campaign-card__content">
        {scrapeKeywords && (
          <div className="campaign-card__tags">
            {scrapeKeywords?.map((tag, index) => (
              <span key={index} className={`tag-badge ${gradientClass}`}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {showPrompt && (
          <div className={`campaign-card__prompt prompt-${gradientClass}`}>
            <span className="prompt-label">Prompt</span>
            <ExpandableText text={queryPrompt} />
          </div>
        )}

        <div className="campaign-card__footer">
          {isLaunched ? (
            <>
              <span className="campaign-state launched">Active</span>

              <Button
                onClick={handleToggleLaunch}
                className="pause-button"
                isLoading={isPending}
              >
                <FaPause className="icon" /> Pause
              </Button>
            </>
          ) : (
            <>
              <div className="campaign-ready">
                <FaClock className="icon" />
                <span>Ready to Launch</span>
              </div>

              <Button
                onClick={handleToggleLaunch}
                className="launch-button"
                isLoading={isPending}
              >
                <FiPlay className="icon" /> Launch
              </Button>
            </>
          )}
        </div>
      </div>

      {editModalOpen && (
        <CampaignModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          mode="edit"
          initialValues={{
            ...campaign,
            type: params?.campaignType as CampaignType,
          }}
        />
      )}
    </div>
  );
};

export default CampaignCard;
